import { useState, useEffect } from 'react';
import { Icon } from '../components/Icon.jsx';
import { AlertBanner } from '../components/AlertBanner.jsx';
import { SkeletonForm } from '../components/SkeletonForm.jsx';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';
import { ALL_LANGUAGES, LANGUAGES_WITH_PUBLISHED_KNOWLEDGE } from '../constants.jsx';
import { mockSave } from '../services/botConfigService.js';

function langLabel(l) {
  return l.native === l.name ? l.name : `${l.native} (${l.name})`;
}

export function LanguagePanel({ bot, updateBot, mockErrorOn, notify }) {
  const [loadingList, setLoadingList] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [primary, setPrimary] = useState(bot.primaryLanguage);
  const [supported, setSupported] = useState(bot.supportedLanguages);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  // Checklist §3 Confirmation state: removing a supported language that
  // already has published knowledge in it.
  const [pendingRemoval, setPendingRemoval] = useState(null);

  // Simulates GET of the available language list. Wired to the same
  // top-bar "Simulate API error" toggle every other panel's save uses, so
  // the "Couldn't load languages" state (previously dead code — loadError
  // was never set to true anywhere) is now actually reachable and
  // Retry-able for QA, instead of only ever succeeding after a timeout.
  function loadLanguages() {
    setLoadingList(true);
    setLoadError(false);
    setTimeout(() => {
      if (mockErrorOn) {
        setLoadError(true);
        setLoadingList(false);
      } else {
        setLoadingList(false);
      }
    }, 700);
  }

  useEffect(() => {
    loadLanguages();
    // Intentionally run once on mount; Retry below re-invokes this with
    // whatever mockErrorOn is *at the time of the click*.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function removeLanguage(code) {
    setSupported((s) => s.filter((x) => x !== code));
  }

  function requestToggle(code) {
    if (code === primary || status === 'saving') return;
    if (supported.includes(code)) {
      if (LANGUAGES_WITH_PUBLISHED_KNOWLEDGE.includes(code)) {
        setPendingRemoval(code);
      } else {
        removeLanguage(code);
      }
    } else {
      setSupported((s) => [...s, code]);
    }
  }

  function save() {
    if (!primary) {
      setError('Choose a primary language');
      return;
    }
    setError(null);
    setStatus('saving');
    mockSave(mockErrorOn)
      .then(() => {
        updateBot({ primaryLanguage: primary, supportedLanguages: supported });
        setStatus('idle');
        notify('success', 'Language settings saved');
      })
      .catch(() => setStatus('error'));
  }

  const searchTerm = search.trim().toLowerCase();
  const selectableLanguages = ALL_LANGUAGES.filter((l) => l.code !== primary);
  const filteredLanguages = searchTerm
    ? selectableLanguages.filter(
        (l) => l.name.toLowerCase().includes(searchTerm) || l.native.toLowerCase().includes(searchTerm)
      )
    : selectableLanguages;
  const supportedLanguageObjs = ALL_LANGUAGES.filter((l) => supported.includes(l.code));
  const pendingLanguage = ALL_LANGUAGES.find((l) => l.code === pendingRemoval);

  return (
    <div>
      <div className="panel-header">
        <h1>Language</h1>
        <p>Set the bot's primary language and any additional languages visitors can use.</p>
      </div>
      {status === 'error' && <AlertBanner text="We couldn't save your changes. Try again." onRetry={save} />}
      {loadingList ? (
        <SkeletonForm />
      ) : loadError ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-icon"><Icon.alert s={22} /></div>
            <h3>Couldn't load languages</h3>
            <p>Check your connection and try again.</p>
            <button className="btn-secondary" onClick={loadLanguages}>
              Retry
            </button>
          </div>
        </div>
      ) : (
        <div className="card">
          {!primary && supported.length === 0 && (
            <div className="empty-state" style={{ paddingTop: 0, paddingBottom: 22 }}>
              <p style={{ margin: 0 }}>No languages set — visitors will see English by default.</p>
            </div>
          )}
          <div className={'field' + (error ? ' invalid' : '')}>
            <label htmlFor="primary-lang">Primary language</label>
            <select
              id="primary-lang"
              className="form-select"
              value={primary}
              onChange={(e) => setPrimary(e.target.value)}
              aria-describedby={error ? 'primary-lang-error' : undefined}
            >
              <option value="">Select a language…</option>
              {ALL_LANGUAGES.map((l) => (
                <option key={l.code} value={l.code} dir={l.rtl ? 'rtl' : 'ltr'}>
                  {langLabel(l)}
                </option>
              ))}
            </select>
            {error && (
              <div className="error-text" id="primary-lang-error">
                <Icon.alert s={12} /> {error}
              </div>
            )}
            <div className="hint">Visitors see this language by default.</div>
          </div>

          <div className="field">
            <label htmlFor="lang-search">Additional supported languages</label>

            {/* Bootstrap input-group: search/filter for long language lists
                (checklist §3, previously missing entirely). */}
            <div className="input-group input-group-sm mb-2" style={{ maxWidth: 280 }}>
              <span className="input-group-text"><Icon.search s={12} /></span>
              <input
                id="lang-search"
                type="text"
                className="form-control"
                placeholder="Search languages…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search additional languages"
              />
            </div>

            <div className="row row-cols-auto g-2">
              {filteredLanguages.length === 0 ? (
                <div className="col">
                  <span className="hint">No languages match "{search}"</span>
                </div>
              ) : (
                filteredLanguages.map((l) => (
                  <div className="col" key={l.code}>
                    <button
                      type="button"
                      className={'lang-pill' + (supported.includes(l.code) ? ' added' : '')}
                      onClick={() => requestToggle(l.code)}
                      disabled={status === 'saving'}
                      aria-pressed={supported.includes(l.code)}
                    >
                      {supported.includes(l.code) ? '✓ ' : '+ '}
                      <span dir={l.rtl ? 'rtl' : 'ltr'}>{langLabel(l)}</span>
                    </button>
                  </div>
                ))
              )}
            </div>

            {supportedLanguageObjs.length > 0 && (
              <div className="chip-row">
                {supportedLanguageObjs.map((l) => (
                  <span className="chip" key={l.code}>
                    <span dir={l.rtl ? 'rtl' : 'ltr'}>{langLabel(l)}</span>
                    <button
                      onClick={() => requestToggle(l.code)}
                      disabled={status === 'saving'}
                      aria-label={`Remove ${l.name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="actions-row">
            <button className="btn-primary" disabled={status === 'saving'} onClick={save}>
              {status === 'saving' && (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              )}
              {status === 'saving' ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      {pendingLanguage && (
        <ConfirmDialog
          title="Remove this language?"
          body={`Removing ${pendingLanguage.name} may hide knowledge already published in it. Continue?`}
          confirmLabel="Remove language"
          onConfirm={() => {
            removeLanguage(pendingRemoval);
            setPendingRemoval(null);
          }}
          onCancel={() => setPendingRemoval(null)}
        />
      )}
    </div>
  );
}
